// The catalogue lives in D1 and is managed from /admin, so the storefront
// fetches it from the Worker. Until that answers (or if it is unreachable) the
// bundled seed list is used, so the shop never renders empty.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { products as fallbackProducts, type Product } from '../../data/products'

export interface ShopSettings {
  shopUsername: string | null
  instagram: string | null
  whatsapp: string | null
  phone: string | null
  email: string | null
}

interface CatalogState {
  products: Product[]
  bySlug: (slug: string) => Product | undefined
  featured: Product[]
  newArrivals: Product[]
  settings: ShopSettings
  loading: boolean
  refresh: () => void
}

const emptySettings: ShopSettings = { shopUsername: null, instagram: null, whatsapp: null, phone: null, email: null }

const CatalogContext = createContext<CatalogState | null>(null)

export function CatalogProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<Product[]>(fallbackProducts)
  const [settings, setSettings] = useState<ShopSettings>(emptySettings)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const [list, config] = await Promise.all([
          fetch('/api/products').then((r) => (r.ok ? r.json() : Promise.reject(new Error('products')))) as Promise<{ products: Product[] }>,
          fetch('/api/settings').then((r) => (r.ok ? r.json() : Promise.reject(new Error('settings')))) as Promise<ShopSettings>,
        ])
        if (cancelled) return
        if (Array.isArray(list.products) && list.products.length) setProducts(list.products)
        setSettings({
          shopUsername: config.shopUsername ?? null,
          instagram: config.instagram ?? null,
          whatsapp: config.whatsapp ?? null,
          phone: config.phone ?? null,
          email: config.email ?? null,
        })
      } catch {
        // Offline or a stripped build: keep the bundled catalogue.
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [nonce])

  const value = useMemo<CatalogState>(() => {
    return {
      products,
      bySlug: (slug: string) => products.find((p) => p.slug === slug),
      featured: products.filter((p) => p.featured),
      newArrivals: products.slice(0, 4),
      settings,
      loading,
      refresh: () => setNonce((n) => n + 1),
    }
  }, [products, settings, loading])

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>
}

export function useCatalog(): CatalogState {
  const ctx = useContext(CatalogContext)
  if (!ctx) throw new Error('useCatalog outside CatalogProvider')
  return ctx
}

/** Opens the shop's Telegram user profile, not the ordering bot. */
export function orderUrl(settings: ShopSettings): string | null {
  const username = settings.shopUsername?.replace(/^@/, '')
  return username ? `https://t.me/${username}` : null
}

export function money(value: number): string {
  const [whole, cents] = value.toFixed(value % 1 === 0 ? 0 : 2).split('.')
  return `${whole.replace(/\\B(?=(\\d{3})+(?!\\d))/g, ' ')}${cents ? `,${cents}` : ''} so'm`
}

export const instagramUrl = (handle: string) => `https://instagram.com/${handle.replace(/^@/, '')}`

/** WhatsApp telefon raqamini wa.me havolasiga aylantiradi. */
export function whatsappUrl(phone: string): string {
  const clean = phone.replace(/\D/g, '')
  return `https://wa.me/${clean}`
}

export function phoneUrl(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`
}